import { BindId, type Route } from "@wildlark/relay-client"

const MAX_NEST_ROUTES = 8

export type NestRoute = { type: "direct"; origin: string } | { type: "relay"; relayUrl: string; bindId: string; basePath?: string }

export interface NestConnection {
    routes: NestRoute[]
    token: string
    nodeId: string
    kind?: "local" | "remote"
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function nonEmptyString(value: unknown): string | null {
    return typeof value === "string" && value.trim() ? value.trim() : null
}

function normalizeOrigin(value: string, name: string): string {
    let parsed: URL
    try {
        parsed = new URL(value)
    } catch {
        throw new Error(`${name} must be a valid origin`)
    }
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:" && parsed.protocol !== "ws:" && parsed.protocol !== "wss:") {
        throw new Error(`${name} must use HTTP, HTTPS, WS, or WSS`)
    }
    if (parsed.username || parsed.password || parsed.search || parsed.hash || (parsed.pathname !== "" && parsed.pathname !== "/")) {
        throw new Error(`${name} must contain only an origin`)
    }
    return parsed.origin
}

export function normalizeNestRoute(value: unknown): NestRoute {
    if (!isRecord(value)) throw new Error("Nest route must be an object")
    if (value.type === "direct") {
        const origin = nonEmptyString(value.origin)
        if (!origin) throw new Error("Direct Nest route requires an origin")
        const normalized = normalizeOrigin(origin, "Direct Nest route")
        const protocol = new URL(normalized).protocol
        if (protocol !== "http:" && protocol !== "https:") throw new Error("Direct Nest route must use HTTP or HTTPS")
        return { type: "direct", origin: normalized }
    }
    if (value.type === "relay") {
        const relayUrl = nonEmptyString(value.relayUrl)
        const bindId = nonEmptyString(value.bindId)
        const basePath = nonEmptyString(value.basePath) ?? undefined
        if (!relayUrl || !bindId) throw new Error("Relay Nest route requires a relay URL and bind ID")
        const normalizedRelayURL = normalizeOrigin(relayUrl, "Nest relay URL")
        BindId.parse(bindId)
        if (basePath && (!basePath.startsWith("/") || basePath === "/" || basePath.endsWith("/"))) {
            throw new Error("Nest relay base path must begin with / and have no trailing /")
        }
        return { type: "relay", relayUrl: normalizedRelayURL, bindId, ...(basePath ? { basePath } : {}) }
    }
    throw new Error("Nest route type is invalid")
}

export function normalizeNestRoutes(value: unknown): NestRoute[] {
    if (!Array.isArray(value) || value.length === 0) throw new Error("At least one Nest route is required")
    if (value.length > MAX_NEST_ROUTES) throw new Error(`A Nest connection supports at most ${MAX_NEST_ROUTES} routes`)
    const routes: NestRoute[] = []
    const seen = new Set<string>()
    for (const candidate of value) {
        const route = normalizeNestRoute(candidate)
        const key = nestRouteKey(route)
        if (seen.has(key)) continue
        seen.add(key)
        routes.push(route)
    }
    if (routes.length === 0) throw new Error("At least one Nest route is required")
    return routes
}

export function nestRouteKey(route: NestRoute): string {
    return route.type === "direct" ? `direct:${route.origin}` : `relay:${route.relayUrl}:${route.bindId}:${route.basePath ?? "/v1"}`
}

export function nestRouteLabel(route: NestRoute): string {
    return route.type === "direct" ? route.origin : `${new URL(route.relayUrl).host} · ${route.bindId.slice(0, 10)}…`
}

export function directNestOrigin(routes: readonly NestRoute[]): string | null {
    return routes.find((route): route is Extract<NestRoute, { type: "direct" }> => route.type === "direct")?.origin ?? null
}

function isLoopbackHostname(hostname: string): boolean {
    const normalized = hostname.toLowerCase().replace(/^\[|\]$/g, "")
    return normalized === "localhost" || normalized === "127.0.0.1" || normalized === "::1"
}

export function isShareableNestRoute(route: NestRoute): boolean {
    if (route.type === "relay") {
        const protocol = new URL(route.relayUrl).protocol
        return protocol === "https:" || protocol === "wss:"
    }
    const url = new URL(route.origin)
    return url.protocol === "https:" && !isLoopbackHostname(url.hostname)
}

export function shareableNestRoutes(routes: readonly NestRoute[]): NestRoute[] {
    return routes.filter(isShareableNestRoute)
}

export function blindbindRoute(route: NestRoute): Route {
    if (route.type === "direct") return route
    return {
        type: "relay",
        endpoint: {
            relayUrl: route.relayUrl,
            bindId: BindId.parse(route.bindId),
            ...(route.basePath ? { basePath: route.basePath } : {}),
        },
    }
}
